import React, { useState } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { authAPI } from '../api/client'
import type { TwoFactorSetupResponse } from '../api/types'

/**
 * TwoFactorSetup component provides UI for enabling two-factor authentication.
 *
 * Features:
 * - Generate TOTP secret and QR code
 * - Display QR code for authenticator app scanning
 * - Verify TOTP token to enable 2FA
 * - Display secret key for manual entry
 * - Dark mode support
 * - Loading and error states
 */

interface TwoFactorSetupProps {
  onComplete?: () => void
}

export const TwoFactorSetup: React.FC<TwoFactorSetupProps> = ({ onComplete }) => {
  const [setupData, setSetupData] = useState<TwoFactorSetupResponse | null>(null)
  const [verificationToken, setVerificationToken] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isVerifying, setIsVerifying] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [isSetupStarted, setIsSetupStarted] = useState(false)

  /**
   * Initialize 2FA setup by generating TOTP secret and QR code.
   */
  const handleStartSetup = async () => {
    try {
      setIsLoading(true)
      setError(null)
      setSuccessMessage(null)

      const data = await authAPI.setup2FA()
      setSetupData(data)
      setIsSetupStarted(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to initialize 2FA setup')
    } finally {
      setIsLoading(false)
    }
  }

  /**
   * Verify TOTP token and enable 2FA.
   */
  const handleVerifyToken = async (e: React.FormEvent) => {
    e.preventDefault()

    // Validate token format
    if (!/^\d{6}$/.test(verificationToken)) {
      setError('Token must be 6 digits')
      return
    }

    try {
      setIsVerifying(true)
      setError(null)
      setSuccessMessage(null)

      await authAPI.verify2FA({ token: verificationToken })
      setSuccessMessage('Two-factor authentication enabled successfully!')

      // Clear form after 2 seconds and call onComplete
      setTimeout(() => {
        setVerificationToken('')
        setSetupData(null)
        setIsSetupStarted(false)
        setSuccessMessage(null)
        if (onComplete) {
          onComplete()
        }
      }, 2000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to verify token')
    } finally {
      setIsVerifying(false)
    }
  }

  /**
   * Cancel setup and reset state.
   */
  const handleCancel = () => {
    setSetupData(null)
    setVerificationToken('')
    setIsSetupStarted(false)
    setError(null)
    setSuccessMessage(null)
  }

  /**
   * F08-002: the otpauth:// URI carries the full TOTP secret, so the QR is
   * encoded in the browser (qrcode.react, SVG) and never sent to a QR service.
   */

  return (
    <div data-testid="two-factor-setup" className="card">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
        Two-Factor Authentication
      </h2>

      {/* Error message */}
      {error && (
        <div data-testid="two-factor-error" role="alert" className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* Success message */}
      {successMessage && (
        <div data-testid="two-factor-success" role="status" className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-400 px-4 py-3 rounded mb-4">
          {successMessage}
        </div>
      )}

      {/* Initial state - Start setup button */}
      {!isSetupStarted && (
        <div>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Add an extra layer of security to your account by enabling two-factor authentication.
            You'll need an authenticator app like Google Authenticator or Authy.
          </p>
          <button
            onClick={handleStartSetup}
            disabled={isLoading}
            data-testid="two-factor-start-button"
            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Initializing...' : 'Enable Two-Factor Authentication'}
          </button>
        </div>
      )}

      {/* Setup in progress - Display QR code and verification form */}
      {isSetupStarted && setupData && (
        <div className="space-y-6">
          {/* Step 1: Scan QR Code */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-3">
              Step 1: Scan QR Code
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Open your authenticator app and scan this QR code:
            </p>

            <div className="flex justify-center mb-4">
              <div data-testid="totp-qr" className="bg-white p-4 rounded-lg shadow-sm">
                <QRCodeSVG value={setupData.qr_code_url} size={192} marginSize={2} />
              </div>
            </div>

            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded p-4">
              <p className="text-sm text-blue-900 dark:text-blue-200 font-medium mb-2">
                Can't scan? Enter the secret key directly:
              </p>
              <p className="text-xs text-blue-800 dark:text-blue-300 mb-2">
                Most authenticator apps accept a manual secret entry:
              </p>
              <div data-testid="two-factor-secret" className="bg-white dark:bg-gray-800 rounded px-3 py-2 font-mono text-sm text-gray-900 dark:text-gray-100 break-all">
                {setupData.secret}
              </div>
            </div>
          </div>

          {/* Step 2: Verify Token */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-3">
              Step 2: Verify Token
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Enter the 6-digit code from your authenticator app to complete setup:
            </p>

            <form onSubmit={handleVerifyToken} className="space-y-4">
              <div>
                <label htmlFor="token" className="label">
                  Verification Code
                </label>
                <input
                  type="text"
                  id="token"
                  data-testid="two-factor-token-input"
                  value={verificationToken}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6)
                    setVerificationToken(value)
                    setError(null)
                  }}
                  placeholder="123456"
                  maxLength={6}
                  pattern="\d{6}"
                  className="input max-w-xs"
                  required
                  autoComplete="off"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Enter the 6-digit code shown in your authenticator app
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={isVerifying || verificationToken.length !== 6}
                  data-testid="two-factor-verify-button"
                  className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isVerifying ? 'Verifying...' : 'Verify and Enable'}
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  disabled={isVerifying}
                  data-testid="two-factor-cancel-button"
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
